import React, { useState } from "react";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddRoleForm from "../components/Permissions/AddRoleForm";
import AddUserForm from "../components/Permissions/AddUserForm";
import RoleCard from "../components/Permissions/RoleCard";
import EditRolePermissionsDialog from "../components/Permissions/EditRolePermissionsDialog";
import UserCard from "../components/Permissions/UserCard";
import EditUserDialog from "../components/Permissions/EditUserDialog";
import useFetchWithMsal from "../hooks/useFetchWithMsal";
import { loginRequest, protectedResources } from "../authConfig";
import { useAllBranches } from "../contexts/BranchesContext";
import CustomSnackbar from "../components/CustomSnackbar";
import { usePermissions } from "../components/permissionsHook";

const PermissionsPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [roleSearchTerm, setRoleSearchTerm] = useState("");
  const [editingRoleId, setEditingRoleId] = useState(null);

  const {
    updateRole,
    translation,
    tabValue,
    user,
    usersList,
    innerTabValue,
    filteredTabs,
    handleInnerTabChange,
    handleDeleteUser,
    execute,
    rolesList,
    setRolesList,
    snackbar,
    setSnackbar,
    editUserId,
    setEditUserId,
    showAddRoleForm,
    setShowAddRoleForm,
    showAddUserForm,
    setShowAddUserForm,
    mockDropdownData,
    setMockDropdownData,
    allBranches,
    adminPages,
    translateKey,
    handleCheckboxChange,
    handleAccordionChange,
    handleAddUser,
    pageNames,
    handleAddRole,
    handleTabChange,
    disabledCheckbox,
    getDisplayValue,
    handleDeleteRole,
    handleUpdateUser,
  } = usePermissions({
    backendUrl: process.env.REACT_APP_BACKEND_BASE_URL,
  });

  const isAdmin = user?.roles?.some((r) => r.admin === true);

  if (!rolesList.length) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }
  return (
    <div className="App">
      <Box display="flex" flexDirection="column" gap={4} p={2}>
        <Typography variant="h6" gutterBottom>
          {translation.systemSettings}
        </Typography>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
        >
          <Tab label={translation.permissionsGroups} />
          <Tab label={translation.users} />
          <Tab label={translation.infrastructureData} />
        </Tabs>

        {tabValue === 0 && (
          <Box mt={4}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() =>
                  setSnackbar((prev) => ({ ...prev, open: false, message: "" }))
                }
                autoHideDuration={5000}
              />
              <Typography variant="h6">
                {translation.permissionsGroups}
              </Typography>
              {showAddRoleForm ? (
                <AddRoleForm
                  onAddRole={handleAddRole}
                  onCancel={() => {
                    setShowAddRoleForm(false);
                    setSnackbar({ ...snackbar, open: false });
                  }}
                  pages={adminPages.filter(
                    (p) =>
                      p !== "ViewObligoData" && p !== "ResearchMonitorRole",
                  )}
                />
              ) : (
                <Button
                  onClick={() => {
                    setShowAddRoleForm(true);
                    setSnackbar({ ...snackbar, open: false });
                  }}
                  variant="contained"
                  color="primary"
                >
                  {translation.addRole}
                </Button>
              )}
            </Box>

            <Box sx={{ mb: 3 }}>
              <TextField
                placeholder="חיפוש תפקיד..."
                value={roleSearchTerm}
                onChange={(e) => setRoleSearchTerm(e.target.value)}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 300 }}
              />
            </Box>

            <Grid container spacing={3}>
              {rolesList
                .filter((role) =>
                  user?.accessRights?.admin === true
                    ? true
                    : Object.keys(user.accessRights).some(
                        (key) =>
                          user.accessRights[key] === true && role[key] === true,
                      ),
                )
                .filter((role) =>
                  role?.Name?.toLowerCase().includes(roleSearchTerm.trim().toLowerCase()),
                )
                .map((role) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={role?._id}>
                    <RoleCard
                      role={role}
                      pageNames={pageNames}
                      translation={translation}
                      onEdit={(r) => setEditingRoleId(r._id)}
                      onDelete={handleDeleteRole}
                    />
                  </Grid>
                ))}
            </Grid>

            <EditRolePermissionsDialog
              role={rolesList.find((r) => r._id === editingRoleId) || null}
              open={!!editingRoleId}
              onClose={() => setEditingRoleId(null)}
              pageNames={pageNames}
              translation={translation}
              handleCheckboxChange={handleCheckboxChange}
              disabledCheckbox={disabledCheckbox}
              updateRole={updateRole}
              allBranches={allBranches}
              translateKey={translateKey}
              getDisplayValue={getDisplayValue}
              mockDropdownData={mockDropdownData}
              setSnackbar={setSnackbar}
              snackbar={snackbar}
              rolesList={rolesList}
              setRolesList={setRolesList}
              execute={execute}
            />
          </Box>
        )}

        {tabValue === 1 && (
          <Box mt={4}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() =>
                  setSnackbar((prev) => ({ ...prev, open: false, message: "" }))
                }
                autoHideDuration={5000}
              />
              <Typography variant="h6">{translation.usersTab}</Typography>
              {showAddUserForm ? (
                <AddUserForm
                  branchesList={allBranches}
                  rolesList={rolesList}
                  onAddUser={handleAddUser}
                  onCancel={() => {
                    setShowAddUserForm(false);
                    setSnackbar({ ...snackbar, open: false });
                  }}
                />
              ) : (
                <Button
                  onClick={() => {
                    setShowAddUserForm(true);
                    setSnackbar({ ...snackbar, open: false });
                  }}
                  variant="contained"
                  color="primary"
                >
                  {translation.addUser}
                </Button>
              )}
            </Box>

            <Box sx={{ mb: 3 }}>
              <TextField
                placeholder="חיפוש משתמשים..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 300 }}
              />
            </Box>

            <Grid container spacing={3}>
              {usersList &&
                usersList
                  .filter(
                    (listUser) =>
                      user.roles.some((role) => role.admin === true) ||
                      listUser.Role.some((roleId) =>
                        user.roles.some((userRole) => userRole._id === roleId),
                      ),
                  )
                  .filter((listUser) => {
                    if (!searchTerm.trim()) return true;
                    const search = searchTerm.toLowerCase();
                    return (
                      listUser.Name?.toLowerCase().includes(search) ||
                      listUser.LastName?.toLowerCase().includes(search) ||
                      listUser.Email?.toLowerCase().includes(search) ||
                      listUser.PriorityUserName?.toLowerCase().includes(search) ||
                      `${listUser.Name} ${listUser.LastName}`.toLowerCase().includes(search)
                    );
                  })
                  .map((listUser) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={listUser?._id}>
                      <UserCard
                        user={listUser}
                        rolesList={rolesList}
                        onEdit={(u) => setEditUserId(u._id)}
                        onDelete={handleDeleteUser}
                      />
                    </Grid>
                  ))}
            </Grid>

            <EditUserDialog
              user={usersList.find((u) => u._id === editUserId) || null}
              open={!!editUserId}
              onClose={() => {
                setEditUserId(null);
                setSnackbar({ ...snackbar, open: false });
              }}
              onUpdateUser={handleUpdateUser}
              rolesList={rolesList}
              isAdmin={isAdmin}
            />
          </Box>
        )}

        {tabValue === 2 && (
          <Box mt={4}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() =>
                  setSnackbar((prev) => ({ ...prev, open: false, message: "" }))
                }
                autoHideDuration={5000}
              />
              <Typography variant="h6">
                {translation.infrastructureData}
              </Typography>
            </Box>

            <Tabs
              value={innerTabValue}
              onChange={handleInnerTabChange}
              indicatorColor="primary"
            >
              {filteredTabs.map((tab, index) => (
                <Tab key={index} label={tab.label} />
              ))}
            </Tabs>

            {filteredTabs[innerTabValue]?.component}
          </Box>
        )}
      </Box>
    </div>
  );
};
export default PermissionsPage;
